import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';
import { cn } from '@/lib/utils';

const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID;

const Checkout = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [plan, setPlan] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: user?.full_name || '',
    email: user?.email || '',
    phone: '',
  });

  const [isEnrolled, setIsEnrolled] = useState(false);

  useEffect(() => {
    const fetchPlanAndEnrollment = async () => {
      if (!planId || !user) {
        if (!planId) return;
        // Just fetch plan if not logged in
        const { data } = await supabase.from('plans').select('*').eq('plan_key', planId.toLowerCase()).maybeSingle();
        setPlan(data);
        setIsLoading(false);
        return;
      }

      // Fetch plan and enrollment status in parallel
      const [planRes, enrollRes] = await Promise.all([
        supabase.from('plans').select('*').eq('plan_key', planId.toLowerCase()).maybeSingle(),
        supabase.from('enrollments').select('id').eq('user_id', user.id).eq('course_id', planId).maybeSingle() // Note: course_id check might need plan.course_id
      ]);

      if (planRes.data) {
        setPlan(planRes.data);
        // Better check: use plan.course_id for enrollment check
        const { data: enrollment } = await supabase
          .from('enrollments')
          .select('id')
          .eq('user_id', user.id)
          .eq('course_id', planRes.data.course_id)
          .eq('status', 'active')
          .maybeSingle();

        if (enrollment) setIsEnrolled(true);
      }

      setIsLoading(false);
    };
    fetchPlanAndEnrollment();
  }, [planId, user]);

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return navigate('/login');
    if (!plan || isEnrolled) return;

    setIsProcessing(true);

    const isLoaded = await loadRazorpay();
    if (!isLoaded) {
      alert("Razorpay SDK failed to load. Please check your connection.");
      setIsProcessing(false);
      return;
    }

    try {
      // Step 1: Create a Pending Payment Record
      const { data: paymentRecord, error: paymentError } = await supabase
        .from('payments')
        .insert({
          user_id: user.id,
          course_id: plan.course_id,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          amount: plan.price,
          status: 'pending',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: plan.price * 100,
        currency: "INR",
        name: "Ratchagan Analysis",
        description: `${plan.name} Plan`,
        image: "/logo.png",
        order_id: "", // Since we don't have a backend to create an order_id yet, we use standard checkout
        handler: async (response: any) => {
          try {
            // Step 2: Update Payment to Success
            const { error: updateError } = await supabase
              .from('payments')
              .update({
                status: 'success',
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                paid_at: new Date().toISOString()
              })
              .eq('id', paymentRecord.id);

            if (updateError) throw updateError;

            // Step 3: Check and Create Enrollment
            const { data: existing, error: checkError } = await supabase
              .from('enrollments')
              .select('id')
              .eq('user_id', user.id)
              .eq('course_id', plan.course_id)
              .maybeSingle();

            if (checkError) throw checkError;

            if (!existing) {
              const { error: enrollError } = await supabase
                .from('enrollments')
                .insert({
                  user_id: user.id,
                  course_id: plan.course_id,
                  name: formData.name, // Added back to match potential schema required fields
                  email: formData.email,
                  phone: formData.phone,
                  status: "active",
                  enrolled_at: new Date().toISOString()
                });

              if (enrollError) throw enrollError;
            }

            toast({ title: "Payment Successful", description: "Welcome to the learning vault!" });
            navigate('/my-courses');
          } catch (dbError: any) {
            console.error("Payment Success Process Failed:", dbError);
            toast({
              variant: "destructive",
              title: "System Error",
              description: dbError.message || "Failed to finalize enrollment. Please contact support."
            });
            setIsProcessing(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: { color: "#c9a227" },
        modal: {
          ondismiss: async () => {
            setIsProcessing(false);
            // Optional: Mark as failed if dismissed? 
            // Usually we mark as failed if explicitly failed, but pending is okay for dismiss.
          }
        }
      };

      const rzp = new (window as any).Razorpay(options);

      // Handle explicit payment failure
      rzp.on('payment.failed', async (response: any) => {
        await supabase
          .from('payments')
          .update({
            status: 'failed',
            razorpay_payment_id: response.error.metadata.payment_id
          })
          .eq('id', paymentRecord.id);

        toast({ variant: "destructive", title: "Payment Failed", description: response.error.description });
        setIsProcessing(false);
      });

      rzp.open();
    } catch (err: any) {
      alert(`Initialization Error: ${err.message}`);
      setIsProcessing(false);
    }
  };

  if (isLoading) return <Layout><div className="flex items-center justify-center p-20 text-muted-foreground">Loading plan details...</div></Layout>;
  if (!plan) return <Layout><div className="p-20 text-center">Plan not found. <Link to="/courses" className="text-primary underline">Explore Courses</Link></div></Layout>;

  return (
    <Layout>
      <section className="py-20">
        <div className="container max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10">
            <div className="p-8 bg-white/40 border border-white/60 rounded-3xl backdrop-blur-sm self-start">
              <h2 className="text-xl font-bold mb-6 text-primary uppercase">Summary</h2>
              <div className="p-5 bg-primary/5 rounded-2xl border border-primary/10 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-primary">{plan.name}</span>
                  <span className="text-lg font-bold text-primary">₹{plan.price.toLocaleString()}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{plan.duration}</p>
              </div>
              <ul className="space-y-3 mb-6">
                {plan.features?.map((f: string, i: number) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-accent" /> {f}
                  </li>
                ))}
              </ul>
              <div className="pt-6 border-t border-gray-100 flex justify-between font-bold text-primary uppercase">
                <span>Total</span>
                <span>₹{plan.price.toLocaleString()}</span>
              </div>
            </div>
            <div className="p-8 bg-white/60 border border-white/60 rounded-3xl backdrop-blur-sm shadow-xl">
              <h2 className="text-xl font-bold mb-6 text-primary uppercase">Details</h2>
              <form onSubmit={handlePayment} className="space-y-4">
                <Input placeholder="Full Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required className="h-12 bg-white/50" />
                <Input readOnly type="email" placeholder="Email" value={formData.email} className="h-12 bg-gray-50/50 cursor-not-allowed" />
                <Input type="tel" placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} required className="h-12 bg-white/50" />
                <Button
                  type="submit"
                  disabled={isProcessing || isEnrolled}
                  className={cn(
                    "w-full h-14 font-bold uppercase text-xs",
                    isEnrolled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "btn-gold"
                  )}
                >
                  {isEnrolled ? 'Already Enrolled' : isProcessing ? 'Connecting...' : 'Complete Payment'}
                </Button>
                <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-muted-foreground uppercase py-2">
                  <Shield className="w-3 h-3 text-accent" /> Secure via Razorpay
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Checkout;
